import clsx from 'clsx';
import { type MouseEvent, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Button } from '@flintmint/web-shared/components/button';
import { Divider } from '@flintmint/web-shared/components/divider';
import { useToaster } from '@flintmint/web-shared/hooks/use-toaster';

import { AppToaster } from '@/components/app-toaster';
import { Logo } from '@/components/logo';

import { appRoutes } from '@/constants/routes';

import { agreements } from './agreements';
import { AgreementView } from './agreement-view';

import styles from './agreements-screen.module.scss';

export const AgreementsScreen = ({ onAccept }: AgreementsScreenProps) => {
  const [accepting, setAccepting] = useState(false);
  const { showToastNotification } = useToaster();
  const { pathname, search, hash, state } = useLocation();
  const navigate = useNavigate();
  // Every view but the summary lives in its own history entry at the same location, so the
  // browser's Back returns to the summary instead of leaving the app.
  const shown = historyView(state) ?? 'summary';
  const reading = shown === 'summary' || shown === 'declined' ? undefined : shown;
  const contentRef = useRef<HTMLDivElement>(null);
  const focusedView = useRef(shown);

  // Switching views unmounts the control that was activated, which would drop focus to the body:
  // move it to the new view's heading instead, so keyboard and screen-reader users land on it.
  useEffect(() => {
    if (focusedView.current === shown) return;
    focusedView.current = shown;
    contentRef.current?.querySelector<HTMLElement>('[data-view-heading]')?.focus();
  }, [shown]);

  const openView = (agreementsView: HistoryView) => {
    // flushSync makes the navigation commit the new view before its promise settles, which is when
    // the browser snapshots the new state.
    const push = () =>
      navigate({ pathname, search, hash }, { state: { agreementsView } satisfies AgreementsHistoryState, flushSync: true });
    if (!('startViewTransition' in document)) {
      void push();
      return;
    }
    document.startViewTransition(push);
  };

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await onAccept();
    } catch {
      showToastNotification({
        type: 'error',
        title: 'We couldn’t complete that just now. Please try again.',
      });
      setAccepting(false);
    }
  };

  return (
    <div className={styles.agreementsScreen}>
      <div className={clsx(styles.card, { [styles.wide]: reading })}>
        <div key={shown} ref={contentRef} className={styles.content}>
          {shown === 'summary' && (
            <SummaryView accepting={accepting} onAccept={handleAccept} onRead={openView} onDecline={() => openView('declined')} />
          )}
          {shown === 'declined' && <DeclinedView onReview={() => navigate(-1)} />}
          {reading && <AgreementView agreement={agreements[reading]} onBack={() => navigate(-1)} />}
        </div>
      </div>
      <AppToaster />
    </div>
  );
};

interface AgreementsScreenProps {
  onAccept: () => Promise<void>;
}

type AgreementKey = keyof typeof agreements;

type HistoryView = AgreementKey | 'declined';

interface AgreementsHistoryState {
  agreementsView: HistoryView;
}

// History state survives a reload and session restore, so it can hold a view this version does not
// know, e.g. an agreement key since renamed: anything else is the summary.
const historyView = (state: unknown): HistoryView | undefined => {
  const view = (state as Partial<AgreementsHistoryState> | null)?.agreementsView;
  if (view === 'declined' || (typeof view === 'string' && Object.hasOwn(agreements, view))) return view;
  return undefined;
};

const SummaryView = (props: {
  accepting: boolean;
  onAccept: () => void;
  onRead: (key: AgreementKey) => void;
  onDecline: () => void;
}) => {
  const { accepting, onAccept, onRead, onDecline } = props;

  // A plain click reads the agreement in place; modified clicks and the context menu fall through to
  // the browser, which opens the standalone page in a new tab.
  const readInPlace = (agreement: AgreementKey) => (event: MouseEvent<HTMLElement>) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    onRead(agreement);
  };

  return (
    <>
      <CardHeader
        heading="Before you begin"
        description="Review and accept both agreements to create your account and start using DocuMint."
      />
      <div className={styles.infoBox}>
        <strong>Your agreements</strong>
        {Object.values(agreements).map(({ title, label, effective }) => (
          <div key={label}>
            <p>
              {title} ({label})
            </p>
            <p className={styles.muted}>{effective}</p>
          </div>
        ))}
      </div>
      <div className={styles.readButtons}>
        <Button to={agreements.eula.route} color="grey" size="big" fullWidth onClick={readInPlace('eula')}>
          Read EULA
        </Button>
        <Button to={agreements.baa.route} color="grey" size="big" fullWidth onClick={readInPlace('baa')}>
          Read BAA
        </Button>
      </div>
      <p className={clsx(styles.small, styles.centered)}>
        By selecting “Accept and continue”, you accept both the{' '}
        <Link to={agreements.eula.route} className={styles.inlineLink} onClick={readInPlace('eula')}>
          {agreements.eula.label}
        </Link>{' '}
        and{' '}
        <Link to={agreements.baa.route} className={styles.inlineLink} onClick={readInPlace('baa')}>
          {agreements.baa.label}
        </Link>
        . Copies will be available in your account.
      </p>
      <div className={styles.actions}>
        <Button size="big" fullWidth loading={accepting} onClick={onAccept}>
          Accept and continue
        </Button>
        <Button appearance="plain" onClick={onDecline}>
          Decline
        </Button>
      </div>
    </>
  );
};

const CardHeader = ({ heading, description }: { heading: string; description: string }) => (
  <>
    <Logo />
    <Divider orientation="horizontal" />
    <div className={styles.heading}>
      <h1 tabIndex={-1} data-view-heading>
        {heading}
      </h1>
      <p className={clsx(styles.small, styles.centered)}>{description}</p>
    </div>
  </>
);

const DeclinedView = ({ onReview }: { onReview: () => void }) => {
  const navigate = useNavigate();

  return (
    <>
      <CardHeader heading="Agreements are required" description="Accept the EULA and BAA to use DocuMint, including Basic." />
      <div className={styles.infoBox}>
        <strong>Your account setup is saved</strong>
        <p>You have not started a trial or subscription.</p>
        <p>You can read the agreements again whenever you’re ready.</p>
      </div>
      <div className={styles.actions}>
        <Button size="big" fullWidth onClick={onReview}>
          Review agreements
        </Button>
        <Button appearance="plain" onClick={() => navigate(appRoutes.logout)}>
          Sign out
        </Button>
      </div>
    </>
  );
};
